/** Risk intake report types, categories, and cascaded sub-categories (Kenvue list). */

export const REPORT_TYPES = [
  'Adhoc Request (USD 35 per company)',
  'One Time Financial Health Assessment (USD 425 per company)',
  'One Time Detailed Supplier Risk Dive (USD 725 per company)',
] as const

export type ReportType = (typeof REPORT_TYPES)[number]

export const OBJECTIVES = [
  'RFI',
  'RFP',
  'Contract Renewal',
  'Contract Negotiations',
  'Onboarding',
  'Anything else',
] as const

export const SOURCING = ['Single', 'Dual', 'Multi'] as const

export const SAMPLE_REPORTS = [
  {
    reportType: REPORT_TYPES[0],
    label: 'Adhoc Risk Assessment',
    href: '/assets/risk-intake-samples/Sample-Adhoc-Risk-Assessment.pdf',
    filename: 'Sample-Adhoc-Risk-Assessment.pdf',
  },
  {
    reportType: REPORT_TYPES[1],
    label: 'Financial Health Assessment',
    href: '/assets/risk-intake-samples/Sample-Financial-Health-Assessment.xlsx',
    filename: 'Sample-Financial-Health-Assessment.xlsx',
  },
  {
    reportType: REPORT_TYPES[2],
    label: 'Detailed Risk Deep Dive',
    href: '/assets/risk-intake-samples/Sample-Detailed-Risk-Deep-Dive.pdf',
    filename: 'Sample-Detailed-Risk-Deep-Dive.pdf',
  },
] as const

/** Category → sub-categories from Kenvue Categories/Sub-categories list */
export const SUBCATEGORIES_BY_CATEGORY: Record<string, string[]> = {
  Chemicals: [
    'CHM - Conditioners / Emollients',
    'CHM - Emulsifiers',
    'CHM - Naturals',
    'CHM - Thickeners',
    'CHM - Alcohols / Solvents',
    'CHM - Fatty Acids & Fatty Alcohols',
    'CHM - Glycerin',
    'CHM - Surfactants',
    'CHM - Corn Derivatives and Sweeteners',
    'CHM - Actives',
    'CHM - Preservatives',
    'CHM - Exfolients',
    'CHM - Acids / Buffers / Salts',
    'CHM - Dyes / Colorants',
    'CHM - Flavors / Fragrances',
    'CHM - Sunscreens',
    'CHM - Vitamins',
    'Bonded NW',
    'CHM - Mineral Oil / Petrolatum',
    'CHM - Silicones & Hydrogels',
    'Baby Hair Care',
    'Capsules',
    'Unclassified API',
    'CHM - Other Chemicals (Waxes & new items)',
    'CHM - Soap Chips',
    'Creams',
    'Toiletries',
    'CHM - Inorganic',
    'OTC',
    'Bar Soaps',
    'Band Aid',
    'CHM - Talc',
    'Pulp',
    'Cotton',
  ],
  'External Manufacturing': ['Others'],
  Packaging: [
    'Apertured Films',
    'Bonded NW',
    'Bottles (HDPE)',
    'Bottles (PET)',
    'Bottles (PP)',
    'Caps/Closures/Lids',
    'Pumps',
    'Corrugated',
    'Cotton',
    'Other nonwovens',
    'Labels',
    'Films (Other)',
    'Films (Polybags)',
    'Films (Laminates)',
    'Films (Paper)',
    'Foils',
    'Injection Molded Components',
    'Jars',
    'Other Packaging',
    'Glass',
    'Barrier & Pouch Films',
    'Pulp',
    'Cartons',
    'Leaflets',
    'Adhesives',
    'Release Paper',
    'Superabsorbents (SAP)',
    'Sleeves',
    'Rayon',
    'Thermoformed Packaging',
    'Metals (Tins and Cans)',
    'Tubes',
    'Composite (Tetrapack)',
    'Airlaid',
    'Band Aid',
    'Toiletries',
    'Creams',
    'OTC',
  ],
  Agencies: [
    'Digital Marketing Services',
    'Global creative services',
    'Marketing sponsorships',
    'PR Agencies',
    'Public Relations & Communications',
    'Shopper Marketing',
  ],
  'Capital Equipment': [
    'Bespoke/special equipment',
    'Building Equipment',
    'Bulk Storage & Handling',
    'Conveyor Systems or Mechanical Product Transfer Equipment',
    'Equipment for Liquids or Semi-Solid Processing or Primary Packaging',
    'Equipment for Parenterals Processing or Primary Packaging',
    'Equipment for Solid-Dose Processing',
    'Injection molding equipment',
    'Other Manufacturing, Process or Packaging Equipment',
    'Packaging Equipment',
    'Process Automation (PLCs, MES)',
  ],
  'Clinical Development': [
    'Biostats, Programming, Medical Writing',
    'Clinical Investigator Payments',
    'Data Management',
    'Development Consulting/KOLs/Advisory Boards',
    'Flexible Clinical/Insourced Staffing',
    'Medical Safety & Pharmacovigilance',
    'Patient Recruitment & Related Printed Materials',
    'Programmatic CRO & ARO',
    'Quality & Compliance',
    'Regulatory Affairs',
  ],
  'Construction & Project Management': [
    'Capital Construction Services',
    'Furniture & Fit out',
  ],
  'Corporate Services': [
    'Corporate Services - Consulting (Non-IT)',
    'Corporate Services - Financial Services',
    'Corporate Services - Financial Services - Audit Services',
    'Corporate Services - Financial Services Tax Consulting',
    'Corporate Services - Legal Services',
    'Corporate Services - Managed Services (Non-IT)',
    'Technical Services - Memberships & Subscriptions',
    'Technical Services - Quality',
  ],
  'Facility Maintenance & Service': [
    'Environmental Health & Safety',
    'Facility Maintenance (HARD)',
    'Facility Services (SOFT)',
    'Integrated Building and Facility Maintenance',
    'Management Services',
    'Office Supplies',
  ],
  'Global Strategic Insights': [
    'Analytics',
    'Primary Market Research',
    'Secondary and Syndicated Data',
  ],
  'HR Services': [
    'HR Services - Contingent Labor',
    'HR Services - Employee Services',
    'HR Services - Learning and Development',
    'HR Services - Talent Acquisition',
    'HR Services - Talent Mobility',
  ],
  'IT APP Dev & Maintenance': [
    'IT Application Development',
    'IT Application Support',
  ],
  'IT Infrastructure': [
    'IT Infrastructure - Data Center Hardware & Services',
    'IT Infrastructure - Help Desk Services',
    'IT Infrastructure - Mobile Communications Products and Services',
    'IT Infrastructure - Network Hardware & Managed Services',
    'IT Infrastructure - Network Transport Data/Voice',
    'IT Infrastructure - Personal Computers',
    'IT Infrastructure - Personal Productivity Solutions',
    'IT Infrastructure - Print Services',
    'IT Infrastructure - Security & Risk Mgt.',
  ],
  'IT Software': [
    'IT Software',
    'IT Software as a Service Subscriptions',
    'IT Software Maintenance',
  ],
  'Maintenance & Repair Operations': [
    'Manufacturing components & supplies',
    'MRO (Maintenance & Repair Operations)',
    'Tooling',
  ],
  'Marketing Operations': [
    'Consumer Promotion & In-Store Displays (POS)',
    'Direct Marketing & Fulfillment',
    'Events, Conventions, Congresses & Logistics (Includes Booths)',
    'Marketing Materials',
  ],
  Media: ['Global Media Agencies'],
  'Professional Education & Access': [
    'Access & Affordability',
    'Professional & Medical Education (Excls. Booth & Logistics)',
  ],
  'Real Estate and Energy': [
    'Energy - Utilities',
    'Energy Management',
    'Real Estate Portfolio Management',
    'Real Estate Transactional Management',
  ],
  Research: [
    'Analytical Development & Testing',
    'Bioanalysis/PK/ADME',
    'Clinical Packaging & Labeling Services',
    'Engineering Services & Product Development',
    'Lab Supplies, Services & Equipment',
    'Packaging & Device Development',
    'Research Consulting/KOLs/Advisory Boards',
    'Research Grants/Sponsorships',
    'Toxicology, Pathology, & Biocompatibility',
  ],
  'Sales & Distributor Services': [
    'Call Centers (including sales, complaints, inquiry)',
    'Distributor Services',
    'eCommerce Services',
    'Field Force Services',
    'Retail Brokers & Merchandising',
  ],
  'Travel, Meeting & Events': [
    'Corporate Aviation Fleet',
    'Hotel (Group meetings & events)',
  ],
}

export const CATEGORIES = Object.keys(SUBCATEGORIES_BY_CATEGORY).sort((a, b) =>
  a.localeCompare(b),
)

export function subcategoriesFor(category: string): string[] {
  return SUBCATEGORIES_BY_CATEGORY[category] || []
}

export function isValidReportType(v: string): v is ReportType {
  return (REPORT_TYPES as readonly string[]).includes(v)
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function parseStakeholderEmails(raw: string): { emails: string[]; error?: string } {
  const parts = String(raw || '')
    .split(/[,;\s]+/)
    .map((p) => p.trim())
    .filter(Boolean)
  if (!parts.length) return { emails: [], error: 'Enter at least one stakeholder email address.' }
  const emails: string[] = []
  const seen = new Set<string>()
  for (const p of parts) {
    if (!EMAIL_RE.test(p)) {
      return { emails: [], error: `Invalid email: ${p}` }
    }
    const k = p.toLowerCase()
    if (seen.has(k)) continue
    seen.add(k)
    emails.push(p)
  }
  return { emails }
}

/** Normalize website to https URL; require hostname with a TLD. */
export function normalizeWebsite(raw: string): { url: string; error?: string } {
  let v = String(raw || '').trim()
  if (!v) return { url: '', error: 'Supplier website is required.' }
  if (!/^https?:\/\//i.test(v)) v = `https://${v}`
  let parsed: URL
  try {
    parsed = new URL(v)
  } catch {
    return { url: '', error: 'Enter a valid website (e.g. https://supplier.com).' }
  }
  if (!['http:', 'https:'].includes(parsed.protocol)) {
    return { url: '', error: 'Website must use http or https.' }
  }
  const host = parsed.hostname
  if (!host || !host.includes('.') || host.startsWith('.') || host.endsWith('.')) {
    return { url: '', error: 'Website must include a domain with a TLD (e.g. .com, .in).' }
  }
  return { url: parsed.toString() }
}
